import React, { useEffect, useState, useRef, useCallback } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import "./ShoppingPage.css";

const ShoppingPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantities, setQuantities] = useState({});
  const productContainerRef = useRef(null);

  // Fetch Products and Categories from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "products"));
        const productList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productList);

        // Extract unique categories from products
        const uniqueCategories = new Set();
        productList.forEach((product) => {
          if (product.Category) {
            uniqueCategories.add(product.Category);
          }
        });

        setCategories(["All", ...Array.from(uniqueCategories)]);

        // Initialize quantities for each product
        const initialQuantities = {};
        productList.forEach((product) => {
          initialQuantities[product.id] = 1;
        });
        setQuantities(initialQuantities);
      } catch (error) {
        console.error("Error fetching products: ", error);
      }
    };

    fetchProducts();
  }, []);

  // Handle Category Change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  // Filter Products by Selected Category
  const filteredProducts = products.filter((product) => {
    if (selectedCategory === "All") {
      return true;
    }
    return product.Category === selectedCategory;
  });

  // Handle Quantity Change
  const handleQuantityChange = (product, newQuantity) => {
    const quantity = Math.max(1, newQuantity);
    setQuantities((prev) => ({
      ...prev,
      [product.id]: quantity,
    }));
    addToCart({ ...product, quantity });
  };

  // Add to Cart
  const handleAddToCart = (product) => {
    addToCart({ ...product, quantity: quantities[product.id] });
  };

  // Infinite Scroll Functionality
  const handleScroll = useCallback(() => {
    if (productContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = productContainerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        // Load more products logic (if needed)
      }
    }
  }, []);

  useEffect(() => {
    const container = productContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <div className="shopping-page">
      <header className="shop-header">
        <h1>Shop Fresh Organic Fruits</h1>
        <p>Choose from a variety of fresh organic fruits delivered to your door.</p>
      </header>

      <div className="main-content">
        {/* Category Sidebar */}
        <aside className="category-sidebar">
          <ul>
            {categories.map((category) => (
              <li
                key={category}
                className={selectedCategory === category ? "active" : ""}
                onClick={() => handleCategoryChange(category)}
              >
                {category}
              </li>
            ))}
          </ul>
        </aside>

        {/* Product Container */}
        <div className="product-container" ref={productContainerRef}>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <div key={product.id} className="product-card">
                {product.image ? (
                  <img src={product.image} alt={product.name} className="product-image" />
                ) : (
                  <p className="no-image">No Image</p>
                )}
                <h3 className="product-name">{product.name}</h3>
                <p className="product-price">Price: ₹{product.price}</p>
                <p className="product-quantity">Quantity: {product.quantity}</p>

                {product.stock > 0 ? (
                  <>
                    <div className="quantity-container">
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(product, quantities[product.id] - 1)}
                        disabled={quantities[product.id] <= 1}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        className="quantity-input"
                        value={quantities[product.id]}
                        onChange={(e) =>
                          handleQuantityChange(product, parseInt(e.target.value, 10) || 1)
                        }
                        min="1"
                      />
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(product, quantities[product.id] + 1)}
                      >
                        +
                      </button>
                    </div>

                    <button className="add-to-cart-btn" onClick={() => handleAddToCart(product)}>
                      🛒 Add to Cart
                    </button>
                  </>
                ) : (
                  <span className="out-of-stock">Out of Stock</span>
                )}
              </div>
            ))
          ) : (
            <p className="no-products">No products available in this category.</p>
          )}
        </div>
      </div>

      <button className="floating-cart-btn" onClick={() => navigate("/cart")}>
        🛒 View Cart
      </button>
    </div>
  );
};

export default ShoppingPage;
